import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SuperAdminLayout from "./SuperAdminLayout";
import {
    fetchCompany,
    fetchPlans,
    changePlan,
    changeStatus,
    setFeatureOverride,
    impersonateCompany,
    deleteTenant
} from "@/services/superadminApi";
import {
    SectionHeader,
    Badge,
    Button,
    CardGlass,
    TablePro,
    LoadingSpinner
} from "@/components/ui/PremiumComponents";
import {
    ArrowLeft,
    Zap,
    Shield,
    Trash2,
    Settings,
    Users,
    Store,
    Printer,
    ExternalLink,
    AlertCircle,
    CheckCircle2,
    Calendar,
    Globe,
    Plus,
    X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Truck } from "lucide-react";

const _raw = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BASE_URL = _raw.replace(/\/api\/?$/, "");

export default function SuperAdminTenantDetail() {
    const { company_id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // States for edits
    const [selectedPlan, setSelectedPlan] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [reason, setReason] = useState("");

    const loadData = async () => {
        setLoading(true);
        try {
            const [detail, plansData] = await Promise.all([
                fetchCompany(company_id),
                fetchPlans()
            ]);
            setData(detail);
            setPlans(plansData.plans || []);
            setSelectedPlan(detail.subscription?.plan_id || "");
            setSelectedStatus(detail.subscription?.status || "active");
        } catch (error) {
            toast.error("Error al cargar detalles del restaurante.");
            navigate("/superadmin/tenants");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [company_id]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const currentPlan = data?.subscription?.plan_id;
            const currentStatus = data?.subscription?.status;

            if (selectedPlan !== currentPlan) {
                await changePlan(company_id, selectedPlan, reason || "Ajuste SuperAdmin");
            }
            if (selectedStatus !== currentStatus) {
                await changeStatus(company_id, selectedStatus, reason || "Ajuste SuperAdmin");
            }

            toast.success("Estructura de suscripción actualizada.");
            loadData();
            setReason("");
        } catch (err) {
            toast.error("No se pudieron aplicar los cambios.");
        } finally {
            setSaving(false);
        }
    };

    const handleAddOverride = async () => {
        const feat = prompt("Nombre técnico de la funcionalidad a forzar:");
        if (!feat) return;
        try {
            await setFeatureOverride(company_id, feat, true);
            toast.success(`Funcionalidad '${feat}' forzada con éxito.`);
            loadData();
        } catch (e) {
            toast.error("Error al añadir override.");
        }
    };

    const handleDeleteOverride = async (feat) => {
        if (!window.confirm(`¿Remover acceso forzado a ${feat}?`)) return;
        try {
            await setFeatureOverride(company_id, feat, null, true);
            toast.success("Override eliminado.");
            loadData();
        } catch (e) {
            toast.error("Error al eliminar override.");
        }
    };

    const handleImpersonate = async () => {
        try {
            const res = await impersonateCompany(company_id);
            const token = res.access_token;
            const subdomain = company.subdomain;

            // Resolve root domain for redirection
            let targetUrl = "";
            const hostname = window.location.hostname;
            
            if (hostname === "localhost" || hostname === "127.0.0.1") {
                // For local dev, we stay on same domain but use query params
                targetUrl = `/auth/callback?token=${token}&tenant=${subdomain}&returnTo=/admin`;
            } else {
                // For production/staging, we go to the subdomain
                const rootDomain = hostname.split('.').slice(-2).join('.');
                targetUrl = `https://${subdomain}.${rootDomain}/auth/callback?token=${token}&returnTo=/admin`;
            }

            toast.info("Accediendo como administrador del restaurante...");
            window.open(targetUrl, "_blank");
        } catch (e) {
            console.error("Impersonation error:", e);
            toast.error("Fallo de impersonación.");
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("¿BORRAR PERMANENTEMENTE? Esta acción no se puede deshacer y borrará todos los datos del cliente.")) return;
        const confirmCode = prompt("Escribe el nombre del restaurante para confirmar:");
        if (confirmCode !== data?.company?.name) return toast.error("Confirmación incorrecta.");

        try {
            await deleteTenant(company_id, true);
            toast.success("Restaurante eliminado del sistema.");
            navigate("/superadmin/tenants");
        } catch (e) {
            toast.error("Error al eliminar.");
        }
    };

    if (loading) return (
        <SuperAdminLayout>
            <div className="flex items-center justify-center p-20">
                <LoadingSpinner size="large" />
            </div>
        </SuperAdminLayout>
    );

    const company = data?.company || {};
    const sub = data?.subscription || {};
    const limits = data?.limits_status || {};

    return (
        <SuperAdminLayout>
            <div className="page-fade-in max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div className="flex items-center gap-6">
                        <button
                            onClick={() => navigate("/superadmin/tenants")}
                            className="p-3 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/5 transition-all text-gray-400 hover:text-white"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-3xl font-black text-white tracking-tighter">
                                    {company.name}
                                </h1>
                                <Badge status={sub.status === 'active' ? 'success' : 'danger'}>
                                    {sub.status?.toUpperCase()}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs font-bold text-gray-500 uppercase tracking-widest">
                                <span>ID: {company_id}</span>
                                <span>•</span>
                                <span className="text-primary">{company.subdomain}.conexopos.com</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Button onClick={handleImpersonate} className="flex items-center gap-2">
                            <ExternalLink size={18} /> Entrar como Admin
                        </Button>
                        <Button variant="secondary" onClick={() => navigate(`/superadmin/auditoria?company_id=${company_id}`)}>
                            <Shield size={18} /> Auditoría
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* LEFT COLUMN: Main Config */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Summary Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CardGlass className="p-5 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                                    <Calendar size={20} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-black text-gray-500 uppercase">Miembro desde</div>
                                    <div className="text-sm font-bold text-white">
                                        {company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'}
                                    </div>
                                </div>
                            </CardGlass>
                            <CardGlass className="p-5 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center text-success">
                                    <Zap size={20} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-black text-gray-500 uppercase">Plan Actual</div>
                                    <div className="text-sm font-bold text-white">
                                        {sub.plan_id?.toUpperCase() || 'STUB'}
                                    </div>
                                </div>
                            </CardGlass>
                            <CardGlass className="p-5 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center text-warning">
                                    <Globe size={20} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-black text-gray-500 uppercase">Sedes Activas</div>
                                    <div className="text-sm font-bold text-white">
                                        {limits.branches?.current || 0} Registradas
                                    </div>
                                </div>
                            </CardGlass>
                        </div>

                        {/* Subscription Management */}
                        <CardGlass className="p-8">
                            <div className="flex items-center gap-2 mb-8">
                                <Settings size={20} className="text-primary" />
                                <h2 className="text-xl font-black text-white tracking-tight">Estructura de Servicios</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Plan de Negocio</label>
                                    <select
                                        value={selectedPlan}
                                        onChange={(e) => setSelectedPlan(e.target.value)}
                                        className="bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
                                    >
                                        {plans.map(p => (
                                            <option key={p.plan_id} value={p.plan_id} className="bg-slate-900">
                                                {p.name} — €{p.price_monthly}/mes
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Estado de Cuenta</label>
                                    <select
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        className={`bg-white/5 border border-white/10 rounded-2xl p-4 font-bold outline-none focus:ring-2 focus:ring-primary/20 appearance-none ${selectedStatus !== 'active' ? 'text-danger' : 'text-success'
                                            }`}
                                    >
                                        <option value="active" className="bg-slate-900 text-success">ACTIVO</option>
                                        <option value="suspended" className="bg-slate-900 text-danger">SUSPENDIDO (Impago)</option>
                                        <option value="trialing" className="bg-slate-900 text-blue-400">PERIODO TRIAL</option>
                                        <option value="canceled" className="bg-slate-900 text-gray-400">CANCELADO</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2 flex flex-col gap-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Justificación del Cambio</label>
                                    <textarea
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Ej: Cambio de plan solicitado por el cliente va WhatsApp..."
                                        className="bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-medium outline-none focus:ring-2 focus:ring-primary/20 h-24"
                                    />
                                </div>
                            </div>

                            {selectedStatus !== 'active' && (
                                <div className="flex items-start gap-3 p-4 bg-danger/5 border border-danger/10 rounded-2xl mb-8">
                                    <AlertCircle size={20} className="text-danger flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="text-sm font-black text-danger uppercase tracking-tight">Acción de Bloqueo Detectada</div>
                                        <p className="text-xs text-danger/70 font-medium">
                                            Al guardar este estado, se restringirá el acceso a todos los módulos operativos y de gestión para este tenant inmediatamente.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-full py-5 text-base shadow-[0_0_20px_rgba(59,130,246,0.2)]"
                            >
                                {saving ? "Sincronizando..." : "Aplicar Actualización Master"}
                            </Button>
                        </CardGlass>

                        {/* Limits and Quotas */}
                        <CardGlass className="p-8">
                            <h3 className="text-lg font-black text-white mb-8 tracking-tight uppercase tracking-widest opacity-80">Uso de Infraestructura</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <QuotaBox icon={<Store size={18} />} label="Sucursales" current={limits.branches?.current} limit={limits.branches?.limit} />
                                <QuotaBox icon={<Users size={18} />} label="Usuarios Staff" current={limits.users?.current} limit={limits.users?.limit} />
                                <QuotaBox icon={<Settings size={18} />} label="Catálogo / Menú" current={limits.menu_items?.current} limit={limits.menu_items?.limit} />
                                <QuotaBox icon={<Printer size={18} />} label="Agentes Print" current={limits.print_agents?.current} limit={limits.print_agents?.limit} />
                            </div>
                        </CardGlass>

                        {/* Delivery & Commissions Config (New) */}
                        <TenantDeliveryConfig companyId={company_id} />
                    </div>

                    {/* RIGHT COLUMN: Overrides & Actions */}
                    <div className="space-y-8">
                        {/* Overrides Card */}
                        <CardGlass className="p-8 border-t-4 border-t-primary">
                            <div className="flex justify-between items-center mb-8">
                                <div className="flex items-center gap-2">
                                    <Zap size={18} className="text-yellow-400" />
                                    <h3 className="text-lg font-black text-white">Manual Overrides</h3>
                                </div>
                                <button
                                    onClick={handleAddOverride}
                                    className="p-1 px-3 bg-primary/10 hover:bg-primary/20 rounded-lg text-primary text-[10px] font-black transition-all"
                                >
                                    + AÑADIR
                                </button>
                            </div>

                            <div className="space-y-4">
                                {sub.feature_overrides && Object.entries(sub.feature_overrides).map(([feat, val]) => (
                                    <div key={feat} className="flex justify-between items-center p-4 bg-white/5 rounded-2xl border border-white/5 group hover:bg-white/10 transition-all">
                                        <div className="flex flex-col">
                                            <span className="text-xs font-black text-white font-mono tracking-tighter uppercase">{feat}</span>
                                            <span className="text-[10px] font-bold text-success mt-1 flex items-center gap-1">
                                                <CheckCircle2 size={10} /> FORCE ACTIVE
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteOverride(feat)}
                                            className="p-2 text-gray-500 hover:text-danger hover:bg-danger/10 rounded-xl transition-all"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                                {(!sub.feature_overrides || Object.keys(sub.feature_overrides).length === 0) && (
                                    <div className="py-10 text-center">
                                        <div className="text-xs font-bold text-gray-600 uppercase tracking-widest">No hay overrides activos</div>
                                    </div>
                                )}
                            </div>
                        </CardGlass>

                        {/* Domain Card */}
                        <CardGlass className="p-8">
                            <h3 className="text-lg font-black text-white mb-6 uppercase tracking-widest opacity-80">Red & Dominio</h3>
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <div className="text-[10px] font-black text-gray-500 uppercase">Subdominio SaaS</div>
                                    <div className="text-sm font-bold text-white font-mono">{company.subdomain}.conexopos.com</div>
                                </div>
                                <div className="space-y-1 pt-4 border-t border-white/5">
                                    <div className="text-[10px] font-black text-gray-500 uppercase">Dominio Custom</div>
                                    <div className="text-sm font-bold text-gray-400">{company.custom_domain || "Sin configurar"}</div>
                                </div>
                            </div>
                        </CardGlass>

                        {/* Danger Zone */}
                        <CardGlass className="p-8 border-danger/20 hover:border-danger/40 transition-all">
                            <h3 className="text-lg font-black text-danger mb-4 flex items-center gap-2">
                                <AlertCircle size={20} /> Danger Zone
                            </h3>
                            <p className="text-xs text-gray-500 font-medium mb-6">
                                Estas acciones son irreversibles. Ten precaución extrema.
                            </p>
                            <button
                                onClick={handleDelete}
                                className="w-full flex items-center justify-center gap-2 py-4 bg-danger/10 hover:bg-danger text-danger hover:text-white border border-danger/20 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
                            >
                                <Trash2 size={16} /> Eliminar permanentemente
                            </button>
                        </CardGlass>
                    </div>
                </div>
            </div>
        </SuperAdminLayout>
    );
}

const TenantDeliveryConfig = ({ companyId }) => {
    const [config, setConfig] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const token = localStorage.getItem("superadmin_token");
                const res = await axios.get(`${BASE_URL}/api/superadmin/settings/delivery/${companyId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setConfig(res.data.local || { company_id: companyId });
            } catch (err) {
                console.error("Error fetching tenant delivery config");
            }
        };
        fetchConfig();
    }, [companyId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem("superadmin_token");
            await axios.patch(`${BASE_URL}/api/superadmin/settings/delivery/${companyId}`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Comisiones del restaurante actualizadas");
        } catch (err) {
            toast.error("Error al actualizar comisiones");
        } finally {
            setSaving(false);
        }
    };

    return (
        <CardGlass className="p-8">
            <div className="flex items-center gap-2 mb-8">
                <Truck size={20} className="text-secondary" />
                <h2 className="text-xl font-black text-white tracking-tight">Comisiones Plataformas Delivery</h2>
            </div>
            <p className="text-xs text-gray-500 mb-6">Configuración individual para este restaurante. Si están vacíos, se usarán los globales.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase">Glovo (%)</label>
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Ej: 30"
                        value={config?.glovo_commission ? (config.glovo_commission * 100).toFixed(2) : ""}
                        onChange={e => setConfig({ ...config, glovo_commission: parseFloat(e.target.value) / 100 })}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase">Uber Eats (%)</label>
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Ej: 30"
                        value={config?.ubereats_commission ? (config.ubereats_commission * 100).toFixed(2) : ""}
                        onChange={e => setConfig({ ...config, ubereats_commission: parseFloat(e.target.value) / 100 })}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase">Just Eat (%)</label>
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Ej: 30"
                        value={config?.justeat_commission ? (config.justeat_commission * 100).toFixed(2) : ""}
                        onChange={e => setConfig({ ...config, justeat_commission: parseFloat(e.target.value) / 100 })}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                    />
                </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full py-4 text-sm font-black uppercase">
                {saving ? "Guardando..." : "Guardar Comisiones Local"}
            </Button>
        </CardGlass>
    );
};

const QuotaBox = ({ icon, label, current = 0, limit = 0 }) => {
    const isInfinite = !limit || limit === 0;
    const progress = isInfinite ? 0 : Math.min(100, (current / limit) * 100);
    const isDanger = progress > 90;

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="text-gray-500">{icon}</div>
                    <span className="text-sm font-bold text-white">{label}</span>
                </div>
                <div className="text-xs font-black tracking-tighter">
                    <span className="text-white">{current}</span>
                    <span className="text-gray-600 mx-1">/</span>
                    <span className="text-gray-400">{isInfinite ? '∞' : limit}</span>
                </div>
            </div>
            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5">
                <div
                    className={`h-full transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(59,130,246,0.3)] ${isDanger ? 'bg-danger' : 'bg-primary'
                        }`}
                    style={{ width: isInfinite ? '100%' : `${progress}%`, opacity: isInfinite ? 0.3 : 1 }}
                />
            </div>
        </div>
    );
};
